/* global Blob */
import {
  getFromStorage,
  removeFromStorage,
  saveToStorage,
  submitMonoliphForm,
  trackError,
} from '@vend/utilities'
import uuid from '@vend/vend-uuid'
import { VdFeatureManager, VdTracking } from '@vend/vend.js'
import angular, { IPromise } from 'angular'
import jitterTime from 'jitter-time'
import each from 'lodash/each'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import isEqual from 'lodash/isEqual'
import md5 from 'md5'
import moment from 'moment'
import VendNumber, { add, round, vn } from 'vend-number'
import type { VdToastNotificationService } from '../vend-ui/types'

import {
  getFormattedReceiptNumberDetails,
  getPromotionSummary,
  hasFulfilmentDetails,
  isClosed,
  isFirstTimeSubmitEarly,
  isQuote,
} from '../functions/sale'

import { client } from '../apollo'
import { WEB_REGISTER } from '../components/web-register.constant'
import {
  EVENT,
  EVENTS,
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN,
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN_PHASE_2,
  FEATURE_ENABLE_CHANGING_FULFILLMENT_TYPES_IN_RETRIEVE_PARKED_SALES,
  SALES_STATUS,
} from '../constants'
import { paymentTypesStore } from '../factories/database/payment-types-store'
import { SalesStore } from '../factories/database/sales-store'
import { SaleTransformer } from '../factories/sale-transformer'
import { doUpdateSale, postEmailReceipt } from '../functions/complete-sale'
import { FulfilmentType } from '../models/fulfilment'
import { Payment } from '../models/payment'
import { Sale } from '../models/sale'
import {
  PARK_SALE_FAILED_ERROR_MSG,
  RETRY_SYNC_STATUSES,
  SaleCreateErrorCode,
  SaleCreateErrorField,
  SaleStatus,
  SaleSyncStatus,
} from '../models/sale.constants'
import {
  $http,
  $interval,
  $log,
  $q,
  $rootScope,
  $window,
} from '../ng-compat/imports'
import {
  createQuoteMutation,
  quoteByIdQuery,
  QuoteDataWithExtras,
} from '../queries/quotes'
import {
  createSaleAddressesMutation,
  SaleAddressMutationData,
} from '../queries/sale-address_create'
import { linkSaleAddressMutation } from '../queries/sale-address_link'
import {
  createSaleMutation,
  GqlFulfilmentDetail,
  GqlPayment,
  SaleMutationData,
  SaleMutationResponse,
} from '../queries/sale_create'
import { EventEmitter } from '../util/event-emitter'
import { getFeatureFromCache } from '../util/feature-cache'
import { OverwritingSaleError } from '../util/overwriting-sale-error'
import { isSameReceiptDetails } from '../util/receipt-compare'
import { SaleCreateCustomError } from '../util/sale-create-custom-error'
import { hasServiceOrder } from '../functions/service'
import { PosStateManager } from './pos-state-manager'
import { RegisterManager } from './register-manager'
import { SyncManager } from './sync-manager'
import { WebRegisterStatusManager } from './web-register-status-manager'

const ERROR_AS_SUCCESS_RESPONSE_CODE = -999
const DUPLICATE_SALE_ID_RESPONSE_CODE = 990
const CREATE_SALE_REQUEST_TIMEOUT_CODE = 900
const MAX_SYNC_RETRIES = 3
const OFFLINE_SALES_STORAGE_ID = 'VEND_WR_OFFLINE_SALES'

interface CreateSaleAddressesResponse {
  data?: {
    createSaleAddresses: {
      billingAddressId: string
      shippingAddressId: string
      shippingAddressToken?: string
      billingAddressToken?: string
      created: boolean
      error?: {
        code: string
      }
    }
  }
}

interface SaleSyncRetryCompleteEvent {
  unsyncedSales: Sale[]
}

interface UnsyncedSalesChangeEvent {
  newValue: $TSFixMe
  prevValue: $TSFixMe
}

interface OfflineSaleData {
  time?: string
  sales?: string[]
}

type Events = {
  saleSyncRetry: []
  saleSyncRetryComplete: [SaleSyncRetryCompleteEvent]
  unsyncedSalesChange: [UnsyncedSalesChangeEvent]
}

export const ERROR_FAILED_TO_RETRIEVE_QUOTE = `Failed to retrieve quote`

/**
 * Posts completed sales to the server.
 */
export class SaleSyncManager extends EventEmitter<Events> {
  private _attemptedSalesCount = 0
  private _currentActiveSync: IPromise<unknown> | null = null
  private _erroredSalesCount = 0
  private _saleSyncRetryInProgress = false
  private _unsyncedSalesCount = 0
  private _uploadSalesCount = 0
  private _whenInitialised?: IPromise<this>
  public erroredSalesFileUrl: $TSFixMe
  public scheduledRetryInterval?: IPromise<unknown> | null
  private _duplicatePaymentLoggedSales = new Set<string>()

  constructor(
    private salesStore: SalesStore,
    private saleTransformer: SaleTransformer,
    private webRegisterStatusManager: WebRegisterStatusManager,
    private posStateManager: PosStateManager,
    private vdTracking: VdTracking,
    wrDebugTools: $TSFixMe,
    private vdWindowDelegate: $TSFixMe,
    private syncManager: SyncManager,
    private vdFeatureManager: VdFeatureManager,
    private registerManager: RegisterManager,
    private readonly vdToastNotificationService: VdToastNotificationService
  ) {
    'ngInject'
    super()
    wrDebugTools.register('saleSyncManager')
  }

  /**
   * Initialise the sale sync manager. If the application is online and there are unsynced sales this will triggered
   * an attempt to resync them.
   *
   * @return {Promise<SaleSyncManager>} A promise resolved with the manager instance was initialisation is complete
   */
  public init(): IPromise<this> {
    if (!this._whenInitialised) {
      this._whenInitialised = $q
        .all([
          this.vdFeatureManager.init(),
          this.webRegisterStatusManager.init(),
        ])
        .then(() => this._getUnsyncedSalesAndUpdateCounts())
        .then(() => {
          this.webRegisterStatusManager.on(
            EVENTS.transitionOnline,
            this._transitionOnline,
            this
          )
          this.webRegisterStatusManager.on(
            EVENTS.transitionOffline,
            this._transitionOffline,
            this
          )

          // Retry unsynced sales as long as we're online (regardless of unsynced sale count) as this is where
          // we apply any clean up code to previously lost/errored sales due to known issues.
          this._retrySalesWhenRegisterReady()

          if (this.webRegisterStatusManager.isOnline()) {
            this._trackSalesMadeOffline()
          } else {
            this._setOfflineStartTime()
          }

          this.syncManager.on('syncComplete', () => {
            // Tracking how many sales are stored locally in indexedDb
            this.salesStore.count().then((count: $TSFixMe) => {
              this.vdTracking.trackUserEvent({
                eventName: 'Local Sales Count',
                metadata: {
                  count,
                  erroredCount: this.erroredSalesCount,
                  attemptedCount: this.attemptedSalesCount,
                  uploadSalesCount: this.uploadSalesCount,
                },
                useBatching: false,
              })
            })

            // Remove old uploaded sales
            this.salesStore.removeUploadedSales()
          })

          return this
        })
    }

    return this._whenInitialised
  }

  /**
   * Identify if a retry of unsynced sales is in progress or scheduled to occur.
   *
   * @property saleSyncRetryInProgress
   * @type {Boolean}
   * @readOnly
   */
  public get saleSyncRetryInProgress(): boolean {
    return this._saleSyncRetryInProgress
  }

  /**
   * The total number of unsynced sales.
   *
   * @property unsyncedSalesCount
   * @type {Number}
   * @readOnly
   */
  public get unsyncedSalesCount(): number {
    return this._unsyncedSalesCount
  }

  /**
   * The number of attempted sales. Those sales that we have attempted to post to the api.
   *
   * @property attemptedSalesCount
   * @type {Number}
   * @readOnly
   */
  public get attemptedSalesCount(): number {
    return this._attemptedSalesCount
  }

  /**
   * The number of errored sales. Those sales that have errored during the upload process.
   *
   * @property erroredSalesCount
   * @type {Number}
   * @readOnly
   */
  public get erroredSalesCount(): number {
    return this._erroredSalesCount
  }

  /**
   * The number of sales waiting to be uploaded for the first time.
   *
   * @property uploadSalesCount
   * @type {Number}
   * @readOnly
   */
  public get uploadSalesCount(): number {
    return this._uploadSalesCount
  }

  /**
   * Collection of unsynced sales count by type.
   *
   * @property unsyncedSaleCountByType
   * @type {Object}
   * @readOnly
   */
  public get unsyncedSaleCountByType() {
    return {
      attemptedSalesCount: this._attemptedSalesCount,
      erroredSalesCount: this._erroredSalesCount,
      uploadSalesCount: this._uploadSalesCount,
    }
  }

  /**
   * Count of failed to sync sales.
   *
   * @property failedToSyncSalesCount
   * @type {Number}
   * @readonly
   */
  public get failedToSyncSalesCount(): number {
    return this._erroredSalesCount + this._attemptedSalesCount
  }

  /**
   * Get the current collection of errored sales; those with an `ERROR` sync status
   * sync status.
   *
   * @return {Promise<Array<Sale>>} A promise resolved with the collection of errored sales
   */
  public getErroredSales(): IPromise<Sale[]> {
    return this._getUnsyncedSalesAndUpdateCounts().then(unsyncedSales =>
      filter(unsyncedSales, sale =>
        Boolean(sale.syncStatus === SaleSyncStatus.ERROR)
      )
    )
  }

  /**
   * Schedule a retry of unsynced sales. Used when a sale/quote fails to post to the server.
   *
   * @param sale  {Sale}
   *        The sale that failed to post to the server
   * @param error
   *        The error that occurred
   * @returns
   */
  private _handleFailedSalePost(sale: Sale, error: $TSFixMe) {
    // Update unsynced, attempted and errored sales count
    this._getUnsyncedSalesAndUpdateCounts().then(() => {
      if (
        this.webRegisterStatusManager.isOnline() &&
        this.vdWindowDelegate.isOnline
      ) {
        return this._scheduleSyncRetry()
      }
    })
    if (sale.status === SaleStatus.PARKED) {
      return $q.reject(PARK_SALE_FAILED_ERROR_MSG)
    }
    return $q.reject(error)
  }

  /**
   * Post a given sale to the server.
   *
   * @param  saleId {String}
   *         The ID (or local ID) of the sale to post to the server.
   *
   * @return {Promise} A promise resolved once the sale has successfully been posted to the server
   */
  public postSale(saleId: string): IPromise<unknown> {
    return this._queueSync(() => {
      return this.salesStore
        .loadSale(saleId)
        .then(async sale => {
          if (!sale) {
            $log.debug(
              '[SaleSyncManager] Sale not found when attempting to load the sale'
            )
            return
          }

          // Deal with Account Balance Payment
          if (sale.isAccountBalancePaymentPaid()) {
            await this._handlePayAccountBalance(sale)
          }
          if (isQuote(sale.status)) {
            return this._doPostQuote(sale).catch((error: $TSFixMe) =>
              this._handleFailedSalePost(sale, error)
            )
          }
          if (sale.syncStatus !== SaleSyncStatus.UPLOADED) {
            return this._doPostSale(sale).catch(error =>
              this._handleFailedSalePost(sale, error)
            )
          }
        })
        .catch(error => {
          // Throw error when parking sale failed
          if (
            typeof error === 'string' &&
            error === PARK_SALE_FAILED_ERROR_MSG
          ) {
            return $q.reject(error)
          } else if (
            error instanceof OverwritingSaleError &&
            error.code === SaleCreateErrorCode.EXISTS &&
            error.sale
          ) {
            $log.debug(error.message)

            // return sale with regenerated localId
            return $q.resolve(error.sale)
          }

          $log.debug('[SaleSyncManager]', error)
        })
    })
  }

  /**
   * Attempts to sync all unsynced sales or a subset (`ATTEMPTED` or `UPLOAD`) of those.
   *
   * @param {Object} options
   *
   *        @param {Boolean} [options.retryAllSales]
   *               Whether all unsynced sales should be retried or just those sales with a sync status of
   * `             `ATTEMPTED` or `UPLOAD`. This allows us to filter out sales that have previously errored when
   *               scheduleding a resync attempt, and also for all unsynced sales to be retrtied when the manager
   *               is initialised.
   *
   * @return {Promise} A promise resolved once all unsynced sales have either been successfully synced or have failed
   *         to sync
   */
  public retryUnsyncedSales(
    options: {
      retryAllSales?: boolean
    } = {}
  ): IPromise<unknown> {
    const { retryAllSales = false } = options

    return this._queueSync(() => {
      this._cancelScheduledSyncRetry()

      this._saleSyncRetryInProgress = true
      this.emit('saleSyncRetry')

      const salesToRetryResolver = retryAllSales
        ? () => this._getUnsyncedSalesAndUpdateCounts()
        : () => this._getRetryableUnSyncedSales()

      return (
        this._findAndFixLostOrErroredSales()
          .then(() => salesToRetryResolver())
          .then(unsyncedSales => {
            return $q(resolve => {
              let postResolver = $q.resolve()

              // Post one sale at a time
              unsyncedSales.forEach(sale => {
                postResolver = postResolver.finally(() =>
                  isQuote(sale.status)
                    ? this._doPostQuote(sale)
                    : this._doPostSale(sale, true)
                )
              })

              // Always resolve the promise, even if syncing the sale failed - this way we continue attempting all the
              // other unsynced sales rather than having the first failure trigger the completion of the resync
              return postResolver.finally(resolve)
            })
          })
          // Get the latest list of unsynced sales from the database (and update the unsyncedSalesCount)
          .then(() => this._getUnsyncedSalesAndUpdateCounts())
          .then(unsyncedSales => {
            this.emit('saleSyncRetryComplete', { unsyncedSales })

            // Clear offline sales data after all sales have been processed
            // TODO: there could be a chance some sales failed again and we will lose the offline track in that case
            if (this.vdFeatureManager.get('track_offline_sale_metadata')) {
              this._clearTrackingDataForOfflineSales()
            }

            // Reset only if we don't have sales that can be uploaded
            if (!this._uploadSalesCount && !this._attemptedSalesCount) {
              this._saleSyncRetryInProgress = false
            } else {
              this._scheduleSyncRetry()
            }

            return unsyncedSales
          })
      )
    })
  }

  /**
   * Generate a downloadable file of errored sales.
   *
   * @param {Object} [RegisterData]
   *        Additional register data to be combined with the downloadable file
   *
   * @return {DOMString/URL} URL to the failed to sync sales file
   */
  public generateErroredSalesFile(registerData: $TSFixMe): IPromise<string> {
    this.clearErroredSalesFile()
    return this.salesStore.getErroredSalesJson().then(erroredSales => {
      angular.merge(registerData, { erroredSales })

      const erroredSalesBlob = new Blob([JSON.stringify(registerData)], {
        type: 'text/json',
      })
      this.erroredSalesFileUrl = $window.URL.createObjectURL(erroredSalesBlob)

      return this.erroredSalesFileUrl
    })
  }

  /**
   * Avoids memory leaks by letting the browser know we dont want to keep the reference to the file any longer. Also
   * needed when replacing a previously generated file.
   */
  public clearErroredSalesFile(): void {
    if (this.erroredSalesFileUrl) {
      $window.URL.revokeObjectURL(this.erroredSalesFileUrl)
    }
  }

  // ============================== Private API - exposed for unit testing ============================== //

  /**
   * Retries all unsynced sales (`ATTEMPTED`, `UPLOAD`, 'ERROR') when the register is in a state to do so.
   */
  private _retrySalesWhenRegisterReady(): void {
    const retryAllSales = { retryAllSales: true }

    if (this.webRegisterStatusManager.isOnline()) {
      if (
        !this.syncManager.syncInProgress() &&
        this.syncManager.syncHasOccurred
      ) {
        this.retryUnsyncedSales(retryAllSales)
      } else {
        this.syncManager.once(
          'syncComplete',
          () => {
            this.retryUnsyncedSales(retryAllSales)
          },
          this
        )
      }
    }
  }

  /**
   * Get the current collection of failed sales that can be retried; those unsynced sales that either have an
   * `ATTEMPTED` or `UPLOAD sale sync status.
   *
   * @return {Promise<Array<Sale>>} A promise resolved with the collection of retryable sales.
   */
  private _getRetryableUnSyncedSales(): IPromise<Sale[]> {
    return this._getUnsyncedSalesAndUpdateCounts().then(unsyncedSales =>
      filter(unsyncedSales, sale =>
        includes(RETRY_SYNC_STATUSES, sale.syncStatus)
      )
    )
  }

  /**
   * Queue a sync operation, ensuring only one is running at a time.
   *
   * @param  {Function} syncFn
   *         The function that will be executed to perform the sync operation - must return a Promise.
   *
   * @return {Promise} A promise that will eventually be settled with the outcome of the sync function promise
   */
  private _queueSync(syncFn: () => IPromise<unknown>): IPromise<unknown> {
    if (this._currentActiveSync) {
      return this._currentActiveSync.finally(() => this._queueSync(syncFn))
    } else {
      this._currentActiveSync = syncFn().finally(() => {
        this._currentActiveSync = null
      })
      return this._currentActiveSync
    }
  }

  /**
   * Schedules a retry of sales; sales with either a sync status of `UPLOAD` or `ATTEMPTED`. Sales with a sync status
   * of `ERROR` are considered unsyncable and require Support intervention.
   */
  private _scheduleSyncRetry(): void {
    const retrySalesCount = this._uploadSalesCount + this._attemptedSalesCount

    if (retrySalesCount === 0 || this.scheduledRetryInterval) {
      return
    }

    this._saleSyncRetryInProgress = true
    this.scheduledRetryInterval = $interval(
      () => this.retryUnsyncedSales(),
      jitterTime(1),
      1
    )
  }

  /**
   * Cancel the scheduled retry of unsynced sales.
   */
  private _cancelScheduledSyncRetry(): void {
    if (this.scheduledRetryInterval) {
      $interval.cancel(this.scheduledRetryInterval)
      this.scheduledRetryInterval = null
      this._saleSyncRetryInProgress = false
    }
  }

  /**
   * Track the state of failed sales.
   *
   * @param sale {Sale}
   *        The sale to evaluate
   */
  private _trackFailedSale({
    sale,
    eventName = 'Failed Sale',
  }: {
    sale: Sale
    eventName?: string
  }): void {
    if (!sale || !(sale instanceof Sale)) {
      return
    }

    const isBlankSale =
      !sale.customer &&
      !sale.hasLineItems() &&
      !sale.hasPayments() &&
      !sale.registerId &&
      !sale.status &&
      !sale.user

    const currentSaleId = this.posStateManager.getActiveSaleLocalId()

    this.vdTracking.trackUserEvent({
      eventName,
      metadata: {
        completedByUser: sale.completedByUser,
        hasCustomer: Boolean(sale.customer),
        hasLineItems: sale.hasLineItems(),
        hasPayments: sale.hasPayments(),
        hasRegisterId: Boolean(sale.registerId),
        hasUser: Boolean(sale.user),
        isBlankSale,
        isCurrentSale: currentSaleId === sale.localId,
        saleLocalId: sale.localId,
        saleDate: sale.saleDate,
        status: sale.status,
        syncAttempts: sale.syncAttempts,
        syncStatus: sale.syncStatus,
      },
    })
  }

  /**
   * Determines if a sale is a retrieved parked sale.
   *
   * @param sale {Sale}
   *        The sale to evaluate
   *
   * @return {boolean} True if the sale is a retrieved parked sale, false otherwise.
   */
  private _isRetrievedParkedSale(sale: Sale): boolean {
    const hasConfirmedLineItems = sale.lineItems.some(
      lineItem => lineItem.confirmed
    )
    return (
      !!(sale.id && !sale.isSavedUnfulfilled) &&
      !hasConfirmedLineItems &&
      getFeatureFromCache(
        FEATURE_ENABLE_CHANGING_FULFILLMENT_TYPES_IN_RETRIEVE_PARKED_SALES
      )
    )
  }

  /**
   * Post the given sale to the API.
   *
   * @param sale {Sale}
   *        The sale to post to the server.
   * @param isRetry {boolean | undefined}
   *        Flag to indicate if it's a retry post.
   *
   * @return {Promise} A promise resolved once the sale has been posted to the server
   */
  private _doPostSale(sale: Sale, isRetry?: boolean): IPromise<unknown> {
    if (this._saleIsIncomplete(sale)) {
      this.vdTracking.trackUserEvent({
        eventName: 'Incomplete Sale Intercepted',
        metadata: {},
      })

      // Silently resolve and prevent post.
      return this._updateStatusOfIncompleteSale(sale)
    }

    sale.metadata = [
      { key: 'app_name', value: WEB_REGISTER.APPLICATION_NAME },
      { key: 'app_version', value: `${WEB_REGISTER.CLIENT_VERSION}` },
    ]

    // Add metadata if the sale was made offline
    if (
      this.vdFeatureManager.get('track_offline_sale_metadata') &&
      this._wasSaleMadeOffline(sale.localId)
    ) {
      sale.metadata.push({ key: 'offline', value: 'true' })
    }

    let saleData

    try {
      saleData = this.saleTransformer.toApi(sale, sale.lineItems)

      const payments = saleData.register_sale_payments || []
      const saleId = sale.id || sale.localId
      const MAX_LOGGED_SALES_CACHE_SIZE = 5000

      /**
       * DUPLICATE PAYMENT DETECTION
       * Identifies sales with anomalous duplicate payments caused by frontend glitches.
       *
       * Criteria:
       * 1. >= 3 Payments: Minimum needed for a [Pay -> Duplicate -> Void] pattern.
       * 2. Net Positive: Positive total exceeds negative total (voids didn't cancel everything).
       * 3. Tight Cluster: All payments occurred within 3 seconds (machine-speed glitch).
       * 4. Dedup: Logs once per session per sale; skipped on retries to prevent spam.
       */
      if (
        this._duplicatePaymentLoggedSales.size > MAX_LOGGED_SALES_CACHE_SIZE
      ) {
        this._duplicatePaymentLoggedSales.clear()
      }
      // Only check for duplicate payments if an anomaly has not been logged for this sale and if it's not a retry and there are at least 3 payments
      if (
        !this._duplicatePaymentLoggedSales.has(saleId) &&
        !isRetry &&
        payments.length >= 3
      ) {
        const PAYMENT_CLUSTER_THRESHOLD_MS = 3000
        // Group payments by positive and negative amounts
        const paymentCategories: {
          positivePayments: Array<Payment & { [key: string]: any }>
          negativePayments: Array<Payment & { [key: string]: any }>
          paymentTypeIds: Set<string>
        } = {
          positivePayments: [],
          negativePayments: [],
          paymentTypeIds: new Set(),
        }

        payments.forEach((payment: any) => {
          if (Number(payment.amount) > 0) {
            paymentCategories.positivePayments.push(payment)
          } else if (Number(payment.amount) < 0) {
            paymentCategories.negativePayments.push(payment)
          }
          paymentCategories.paymentTypeIds.add(payment.payment_type_id)
        })
        if (paymentCategories.negativePayments.length > 0) {
          const {
            positivePayments,
            negativePayments,
            paymentTypeIds,
          } = paymentCategories

          const posSum = positivePayments.reduce(
            (sum: number, p: any) => sum + Math.abs(Number(p.amount)),
            0
          )
          const negSum = negativePayments.reduce(
            (sum: number, n: any) => sum + Math.abs(Number(n.amount)),
            0
          )

          // Pattern: All negatives are cancelled out by positives, with positive remainder
          // This indicates duplicate payments where some were voided but extras remain
          if (posSum > negSum) {
            const allPaymentDates = [
              ...positivePayments,
              ...negativePayments,
            ].map((p: any) => moment(p.payment_date).valueOf())
            const maxDate = Math.max(...allPaymentDates)
            const minDate = Math.min(...allPaymentDates)

            // Check if the spread of ALL payments is within 3 seconds (3000ms)
            const isTightCluster =
              maxDate - minDate <= PAYMENT_CLUSTER_THRESHOLD_MS
            // Only flag when ALL positive payments share a single amount and
            // a negative payment matches that exact amount. This filters out
            // legitimate split payments (e.g., $0.01 + $1.25 + $0.01 with a
            // -$0.01 void) which are not duplicates of the same charge.
            const uniquePositiveAmounts = new Set(
              positivePayments.map(p => Math.abs(Number(p.amount)))
            )
            const allPositiveSameAmount = uniquePositiveAmounts.size === 1
            const positiveAmount = allPositiveSameAmount
              ? uniquePositiveAmounts.values().next().value
              : null
            const hasNegativeMatchingAllPositives =
              allPositiveSameAmount &&
              negativePayments.some(
                n => Math.abs(Number(n.amount)) === positiveAmount
              )

            const hasSamePaymentType = paymentTypeIds.size === 1
            if (isTightCluster && hasNegativeMatchingAllPositives) {
              this._duplicatePaymentLoggedSales.add(saleId)
              // Prepare tags for Raygun filtering
              const tags = [
                'duplicate_payment_anomaly_sale_sync',
                ...(hasSamePaymentType
                  ? [
                      `sale_sync_payment_type_id:${
                        paymentTypeIds.values().next().value
                      }`,
                    ]
                  : []),
              ]
              trackError(
                `[SaleSyncManager] Duplicate payments detected`,
                {
                  data: {
                    totalPayments: payments.length,
                    saleId: sale.id,
                    localSaleId: sale.localId,
                    registerId: sale.registerId,
                    netAmount: posSum - negSum,
                    positivePayments,
                    negativePayments,
                    utcSaleDate: moment(sale.saleDate).utc().format(),
                    saleDate: sale.saleDate,
                    isRetry,
                    clientVersion: WEB_REGISTER.CLIENT_VERSION,
                  },
                },
                tags
              )
            }
          }
        }
      }
    } catch (error: any) {
      // The sale is in a corrupt state, so load the raw sale json and set it's sync status to errored
      return this._setErroredSyncStatusAndLog({ sale, error }).finally(() =>
        $q.reject(error)
      )
    }

    $log.debug('[SaleSyncManager] Posting sale data to server: ', saleData)

    // Put client id in payload to help BE log to identify if the register_sales requests are from same client
    saleData = {
      ...saleData,
      client_id: this.posStateManager.getClientId() || '',
    }

    const isCreatingNewSale =
      !sale.id && this.vdFeatureManager.get('use_create_sale_endpoint')

    const isRetrievedParkedSale = this._isRetrievedParkedSale(sale)

    const isDeliveryFulfillmentType =
      sale.fulfilmentDetails?.[0]?.type === FulfilmentType.DELIVERY

    const options = {
      isFirstTimeSubmitEarly: isFirstTimeSubmitEarly(sale),
      isRetry,
      isCreatingNewSale,
    }

    // Create new sale goes to create-sale mutation
    // Retrieved parked sales with delivery fulfillment also use this path
    if (
      isCreatingNewSale ||
      (isRetrievedParkedSale && isDeliveryFulfillmentType)
    ) {
      const camelData: SaleMutationData = this.saleTransformer.toSaleMutation(
        sale,
        sale.lineItems
      )

      // Only set mutationID in sale object when first time calling createSaleMutation
      // When retrying, mutationID will read from indexDB
      sale.mutationID = sale.mutationID ?? camelData.mutationID

      if (
        hasFulfilmentDetails(sale) &&
        !hasServiceOrder(sale.serviceFields, sale.customFields) &&
        isDeliveryFulfillmentType &&
        getFeatureFromCache(FEATURE_CAPTURE_ADDRESS_SELL_SCREEN)
      ) {
        return this._createSaleForDelivery(sale, camelData, options)
      }

      const createSaleRequest = $q.resolve(
        client.mutate({
          mutation: createSaleMutation,
          variables: {
            input: {
              ...camelData,
            },
          },
        })
      )

      return this._sendRegisterSaleRequest(createSaleRequest, options)
        .then(response => {
          return this._handleCreateSaleSuccess(sale, response?.data, camelData)
        })
        .catch(err => {
          return this._handleCreateSaleFailure(sale, err)
        })
    }

    // Post the given sale to REST api
    return this._postSaleViaRestApi(sale, saleData, options)
  }

  /**
   * Post the given sale to the REST API (/api/register_sales).
   * Handles the 'x-series-create-retry' header for duplicate-sale prevention
   * and delegates to the common request/response helpers.
   */
  private _postSaleViaRestApi(
    sale: Sale,
    saleData: Record<string, unknown>,
    options: {
      isFirstTimeSubmitEarly: boolean
      isRetry?: boolean
      isCreatingNewSale: boolean
    }
  ): IPromise<unknown> {
    let config = {
      params: { strict: 1 },
      headers: {},
    }
    /**
     * This is a fix for the overwriting/duplicate sale issue
     * When it's retrying creating sale, should add 'x-series-create-retry: true' to request headers
     * Backend will handle the duplicating issue
     */
    if (this.vdFeatureManager.get('validate_new_sale') && !sale.id) {
      config = {
        ...config,
        headers: { 'x-series-create-retry': Boolean(options.isRetry) },
      }
    }

    const postSaleRequest = $http.post('/api/register_sales', saleData, config)
    return this._sendRegisterSaleRequest(postSaleRequest, options)
      .then(response => this._handlePostSaleSuccess(sale, response?.data))
      .catch(response => {
        if (
          response instanceof SaleCreateCustomError &&
          response.customError.code === SaleCreateErrorCode.TIMEOUT
        ) {
          return this._handlePostSaleFailure(
            sale,
            response.customError,
            CREATE_SALE_REQUEST_TIMEOUT_CODE
          )
        }
        return this._handlePostSaleFailure(sale, response.data, response.status)
      })
  }

  /**
   * Post sale by given promise, add timeout limit for the first submission of submit early sales
   */
  private _sendRegisterSaleRequest(
    promiseToCall: IPromise<any>,
    options: {
      isFirstTimeSubmitEarly: boolean
      isRetry?: boolean
      isCreatingNewSale: boolean
    }
  ) {
    const {
      isFirstTimeSubmitEarly,
      isRetry = false,
      isCreatingNewSale,
    } = options

    if (!isRetry && isFirstTimeSubmitEarly) {
      // Only add timeout limit for the first submission of submit early sales
      const MAX_REQUEST_WAITING_TIME = 10000

      const timeOutErrMsg = isCreatingNewSale
        ? 'Create sale failed by timeout'
        : 'Register sale failed by timeout'

      return Promise.race([
        promiseToCall,
        new Promise<undefined>((resolve, reject) => {
          setTimeout(
            () =>
              reject(
                new SaleCreateCustomError(
                  timeOutErrMsg,
                  SaleCreateErrorCode.TIMEOUT
                )
              ),
            MAX_REQUEST_WAITING_TIME
          )
        }),
      ])
    } else {
      return promiseToCall
    }
  }

  /**
   * Post the given sale to the API.
   *
   * @param sale {Sale}
   *        The sale to post to the server.
   *
   * @return {Promise} A promise resolved once the quote has been posted to the server
   */
  private _doPostQuote(sale: Sale): any {
    const saleData = this.saleTransformer.toQuoteApi(sale)

    return $q
      .resolve(
        client.mutate({
          mutation: createQuoteMutation,
          variables: {
            quote: saleData,
            shouldSendEmail: sale.receiptEmailAddress.length > 0,
            templateId: sale.receiptID,
          },
        })
      )
      .then(response => this._handlePostQuoteSuccess(sale, response.data))
      .catch(error => this._handlePostQuoteFailure(sale, error))
  }

  /**
   * Handle sending pay_account_balance request to "/customer/pay/{customerId}"
   */
  private async _handlePayAccountBalance(sale: Sale): Promise<void> {
    const activeOutlet = this.registerManager.getActiveOutlet()
    const currencySymbol = activeOutlet && activeOutlet.currency_symbol

    const isFullyPaid = sale.customer?.balance
      ? vn(add(sale.customer.balance, sale.payAccountBalance!.amount)).isZero()
      : false

    let accountBalancePaymentMetadata = {}

    if (sale.payAccountBalance) {
      const paymentTypeUuid = sale.payAccountBalance.paymentTypeId || ''
      const paymentAmount = vn(sale.payAccountBalance.amount || 0).toNumber()

      let paymentTypeName = ''
      let paymentTypeId = 0
      if (paymentTypeUuid) {
        try {
          const paymentType = await paymentTypesStore.get(paymentTypeUuid, true)
          if (paymentType) {
            paymentTypeName = paymentType.name
            paymentTypeId = paymentType.type_id
          }
        } catch (error) {
          $log.debug(
            '[SaleSyncManager] Failed to get payment type for account balance payment',
            error
          )
        }
      }

      // Set base metadata including default empty string for paymentTypeName
      accountBalancePaymentMetadata = {
        paymentAmount,
        paymentTypeName,
        paymentTypeId,
      }
    }

    this.vdTracking.trackUserEvent({
      eventName: 'Account Balance Payment Made',
      metadata: {
        saleId: sale.localId,
        isFullyPaid,
        paymentsMade: sale.payments.length,
        ...accountBalancePaymentMetadata,
      },
    })

    try {
      const monoliphResponse = await submitMonoliphForm(
        `/customer/pay/${sale.customer?.id}`,
        'vend_customer_balance',
        {
          '[amount]': String(sale.payAccountBalance?.amount),
          '[payment_type_id]': sale.payAccountBalance?.paymentTypeId || '',
          '[register_id]': sale.registerId || '',
        }
      )

      if (monoliphResponse.ok) {
        this.vdToastNotificationService.success({
          message: `${currencySymbol}${sale.payAccountBalance?.amount} is being applied to unpaid On Account and Layby sales.`,
        })
      } else {
        // Track failure
        this.vdTracking.trackUserEvent({
          eventName: 'Account Balance Payment Failed',
          metadata: {
            saleId: sale.localId,
          },
        })

        throw new Error()
      }
    } catch (err) {
      // Track failure
      this.vdTracking.trackUserEvent({
        eventName: 'Account Balance Payment Failed',
        metadata: {
          saleId: sale.localId,
        },
      })

      // Todo: handle paying account balance error
      this.vdToastNotificationService.negative({
        message: `${currencySymbol}${sale.payAccountBalance?.amount} was not able to be applied to unpaid On Account and Layby sales. Please go to Customers to record the payment manually.`,
      })
    }
  }

  /**
   * Tests whether the provided sale is a blank sale or is missing the `status` and `payments`.
   *
   * @return {Boolean} True if the sale is a blank sale or is missing the `status` and `payments` properties,
   *         false otherwise.
   */
  private _saleIsIncomplete(sale: Sale): boolean {
    if (!sale) {
      return true
    }

    const isBlankSale =
      !sale.customer &&
      !sale.hasLineItems() &&
      !sale.hasPayments() &&
      !sale.registerId &&
      !sale.status &&
      !sale.user

    return isBlankSale || (!sale.status && !sale.hasPayments())
  }

  /**
   * Handle a corrupt sale by loading the raw sale json and setting its sync status to be errored.
   *
   * @param {Object} options
   *
   *        @param {Sale} options.sale
   *               The errored sale
   *
   *        @param {error} options.error
   *               The accompanying error to track
   */
  private _setErroredSyncStatusAndLog({
    sale,
    error,
  }: {
    sale: Sale
    error: Error
  }): IPromise<unknown> {
    if (sale.syncStatus === SaleSyncStatus.ERROR) {
      return $q.resolve()
    }

    trackError('[SaleSyncManager] Error transforming sale for Api', {
      data: {
        error,
        sale: sale.toJSON(),
        unsyncedSalesCount: this._unsyncedSalesCount,
      },
    })
    return this.salesStore.loadSaleJson(sale.localId).then(saleJson => {
      saleJson!.syncStatus = SaleSyncStatus.ERROR
      return this.salesStore.writeSaleJson(saleJson!)
    })
  }

  /**
   * Handle a successful posting of a sale to the server. Updates the sale with IDs etc returned by the server
   * and saves it to IndexedDB.
   *
   * @param sale {Sale}
   *        The sale that was posted to the server
   *
   * @param data {Object}
   *        The data returned by the server
   *
   * @return {Promise} A promise resolved once the sale has been updated and saved to IndexedDB
   */
  private _handlePostSaleSuccess(
    sale: Sale,
    data: $TSFixMe
  ): IPromise<unknown> {
    if (!data || data.status === 'error' || !data.register_sale) {
      const message = '[SaleSyncManager] Sale post error received as a success'
      trackError(message, {
        data: {
          sale: sale.toJSON(),
          data,
        },
      })
      return this._handlePostSaleFailure(
        sale,
        data,
        ERROR_AS_SUCCESS_RESPONSE_CODE
      )
    }

    const serverSaleData = data.register_sale

    $log.debug(
      '[SaleSyncManager] Sale post succeeded, data returned: ',
      serverSaleData
    )

    if (isClosed(sale.status) && this.vdFeatureManager.get('promotions')) {
      // Get data for promotions helper metrics UBE
      const promoHelperSuggestion = this.posStateManager.getPromotionSuggestion()
      const promoHelperClicked = this.posStateManager.getPromotionsHelperClicked()
      this.vdTracking.trackUserEvent({
        eventName: 'Promotions Helper Metrics',
        metadata: {
          saleId: serverSaleData.id,
          basketSize: sale.getBasketSize(),
          activePromotions: getPromotionSummary(sale.lineItems).map(
            item => item.id
          ),
          promoHelperClicked,
          promoHelperSuggestion,
        },
      })
    }

    this.posStateManager.resetPromotionsHelperValues()

    // Set syncStatus to UPLOADED_TO_COMPLETE only for submit-early sale that is successful on the first submission
    sale.syncStatus = isFirstTimeSubmitEarly(sale)
      ? SaleSyncStatus.UPLOADED_TO_COMPLETE
      : SaleSyncStatus.UPLOADED

    /**
     * This is a fix for the overwriting sale issue
     * When post sale successfully, update the BE generated id to local sale (only for new sale)
     *
     * @see https://vendhq.atlassian.net/browse/TRADE-1582
     */
    if (
      this.vdFeatureManager.get('create_new_sale_after_validating') &&
      !sale.id && // when local sale without id, means it's a new created sale
      sale.localId !== serverSaleData.id
    ) {
      // need to remove previous saved sale with FE generated localId
      return this.salesStore.removeSale(sale.localId).then(() => {
        sale.localId = serverSaleData.id
        return this.salesStore.saveSale(sale)
      })
    }

    if (
      !isSameReceiptDetails(
        sale.receiptNumberDetails,
        data.register_sale.invoice_number
      ) &&
      sale.syncStatus === SaleSyncStatus.UPLOADED_TO_COMPLETE
    ) {
      const localInvoice = sale.getFormattedReceiptNumberDetails()
      sale.receiptNumberDetails = data.register_sale.invoice_number
      this.vdTracking.trackUserEvent({
        eventName: 'Duplicate invoice - Overwrite invoice number from server',
        metadata: {
          saleId: sale.localId,
          localInvoice,
          resolvedInvoice: data.register_sale.invoice_number,
          syncAttempt: sale.syncAttempts,
          userAgent: navigator.userAgent,
        },
      })
    }
    return this.salesStore.saveSale(sale)
  }

  /**
   * Handle a successful creating of a sale to the graphter. Updates the sale with IDs etc returned by the server
   * and saves it to IndexedDB.
   *
   * @param sale {Sale}
   *        The sale that was posted to the graphter
   *
   * @param data {Object}
   *        The data returned by the graphter
   *
   * @param createSaleMutationRequest {SaleMutationData}
   *        The mutation payload that was sent to create the sale
   *
   * @return {Promise} A promise resolved once the sale has been created and saved to IndexedDB
   */
  private _handleCreateSaleSuccess(
    sale: Sale,
    response: SaleMutationResponse,
    createSaleMutationRequest: SaleMutationData
  ): IPromise<unknown> {
    if (!response || !response.createSale || response.createSale.error) {
      if (response?.createSale?.error) {
        // Throw custom error if an error response was received.
        return $q.reject({
          customError: response.createSale.error,
        })
      }
      // Throw an unknown network error if a response was not received when creating a sale.
      return $q.reject({
        networkError: {
          result: 'unknown error',
          statusCode: 0,
        },
      })
    }
    if (sale.receiptNumberDetails) {
      const formattedReceiptNumber = getFormattedReceiptNumberDetails(sale)
      if (
        formattedReceiptNumber !== response?.createSale?.invoiceNumber &&
        createSaleMutationRequest
      ) {
        const {
          id,
          mutationID,
          invoiceSequence,
          resolveInvoiceNumber,
          invoiceNumber,
        } = createSaleMutationRequest
        trackError('[DEBUG] Invoice Number mismatch detected', {
          data: {
            payload: {
              id,
              mutationID,
              invoiceSequence,
              resolveInvoiceNumber,
              invoiceNumber,
            },
            response: {
              invoiceResponse: response.createSale.invoiceNumber,
              id: response.createSale.saleId,
            },
          },
        })
      }
    }

    // Update possibly unsaved customer/note when it's not 1st submission and got re_creation response from server
    if (!isFirstTimeSubmitEarly(sale) && response.createSale.created) {
      let updateSalePromise = Promise.resolve()

      if (
        sale.hasAddedCustomerOnConfirmation ||
        sale.hasUpdatedNoteOnConfirmation
      ) {
        const saleUpdateInput = this.saleTransformer.formatUpdateSaleInput(
          sale,
          !!sale.hasAddedCustomerOnConfirmation,
          !!sale.hasUpdatedNoteOnConfirmation
        )
        updateSalePromise = doUpdateSale(saleUpdateInput).catch(() => {
          $log.warn('[SaleSyncManager] Failed to update a recreating sale')
        })
      }

      // Send email receipt after updating sale
      updateSalePromise.then(() => {
        postEmailReceipt(sale).catch(() => {
          $log.warn(
            '[SaleSyncManager] Failed to send email receipt for a recreating sale'
          )
        })
      })
    }

    // Track promotion helper UBE
    if (isClosed(sale.status) && this.vdFeatureManager.get('promotions')) {
      // Get data for promotions helper metrics UBE
      const promoHelperSuggestion = this.posStateManager.getPromotionSuggestion()
      const promoHelperClicked = this.posStateManager.getPromotionsHelperClicked()
      this.vdTracking.trackUserEvent({
        eventName: 'Promotions Helper Metrics',
        metadata: {
          saleId: sale.localId,
          basketSize: sale.getBasketSize(),
          activePromotions: getPromotionSummary(sale.lineItems).map(
            item => item.id
          ),
          promoHelperClicked,
          promoHelperSuggestion,
        },
      })
    }
    this.posStateManager.resetPromotionsHelperValues()

    // Update syncStatus and update sale in indexDB
    // Set syncStatus to UPLOADED_TO_COMPLETE only for submit-early sale that is successful on the first submission
    sale.syncStatus = isFirstTimeSubmitEarly(sale)
      ? SaleSyncStatus.UPLOADED_TO_COMPLETE
      : SaleSyncStatus.UPLOADED

    if (
      !isSameReceiptDetails(
        sale.receiptNumberDetails,
        response.createSale.invoiceNumber
      ) &&
      sale.syncStatus === SaleSyncStatus.UPLOADED_TO_COMPLETE
    ) {
      const localInvoice = sale.getFormattedReceiptNumberDetails()
      sale.receiptNumberDetails = response.createSale.invoiceNumber
      this.vdTracking.trackUserEvent({
        eventName: 'Duplicate invoice - Overwrite invoice number from server',
        metadata: {
          saleId: sale.localId,
          localInvoice,
          resolvedInvoice: response.createSale.invoiceNumber,
          userAgent: navigator.userAgent,
          syncAttempt: sale.syncAttempts,
        },
      })
    }

    return this.salesStore.saveSale(sale)
  }

  /**
   * Remove PII data from the sale before logging
   *
   * @param sale {Sale}
   *        The sale that was posted to the graphter
   *
   * @returns {Sale} The sale object with PII data removed
   */
  public _prepareDataForLogging(sale: Sale): Sale {
    if (
      hasFulfilmentDetails(sale) &&
      sale.fulfilmentDetails[0].type === FulfilmentType.DELIVERY &&
      getFeatureFromCache(FEATURE_CAPTURE_ADDRESS_SELL_SCREEN)
    ) {
      return new Sale({
        ...sale,
        fulfilmentDetails: sale?.fulfilmentDetails?.map(fd => ({
          type: fd.type,
          note: fd.note,
          billingAddress: undefined,
          shippingAddress: undefined,
        })),
      })
    }
    return sale
  }

  /**
   * Handle a failure when posting of a sale to the server.
   *
   * @param sale {Sale}
   *        The sale that was posted to the server
   *
   * @param data {Any}
   *        The data or error message returned by the server
   *
   * @param status {Number}
   *        The HTTP status code of the response
   *
   * @return {Promise} A promise resolved once the sale has been updated and saved to IndexedDB
   */
  private _handlePostSaleFailure(
    sale: Sale,
    data: $TSFixMe,
    status: number
  ): IPromise<never> {
    const errorMessage = 'Error received when uploading sale'

    const logError = () => {
      if (status === CREATE_SALE_REQUEST_TIMEOUT_CODE) {
        this._trackTimeoutUBE(sale, 'Timeout when uploading sale')
      } else {
        const message = `[SaleSyncManager] ${errorMessage} [${status}]`
        trackError(message, {
          data: {
            sale: JSON.stringify(this._prepareDataForLogging(sale)),
            responseCode: status,
            attempts: sale.syncAttempts,
            data,
          },
        })
      }
    }

    const setSyncAttemptsAndStatus = () => {
      // To not trigger the resync if the first submission of submit-early-sale failed
      if (isFirstTimeSubmitEarly(sale)) {
        sale.syncStatus = SaleSyncStatus.MODIFIED
        // Clear invoice number in IndexedDB so retry gets fresh one from server.
        // Redux state is kept intact for printing (print reads from Redux, not IndexedDB).
        sale.receiptNumberDetails = undefined
        return
      }

      if (sale.syncStatus !== SaleSyncStatus.ERROR) {
        sale.syncAttempts++

        if (sale.syncAttempts === MAX_SYNC_RETRIES) {
          sale.syncStatus = SaleSyncStatus.ERROR
        } else {
          sale.syncStatus = SaleSyncStatus.ATTEMPTED
        }
      }

      logError()
    }
    this._trackFailedSale({ sale })
    ;(sale as $TSFixMe).responseStatus = status
    ;(sale as $TSFixMe).responseData = data

    switch (status) {
      case -1: // Client side canceled
      case 0: // Client side timeout / abort
        if (
          !this.webRegisterStatusManager.isOnline() ||
          !this.vdWindowDelegate.isOnline
        ) {
          this._resetSaleSyncStatus(sale)
          this._incrementSalesMadeOffline(sale.localId)
        } else {
          setSyncAttemptsAndStatus()
        }
        break

      case 401: // Not authorized
        // Redirect should be triggered by the redirectSigninOn401 $http interceptor. Update sale sync status so it
        // can be resynced just in case.
        setSyncAttemptsAndStatus()
        break

      case 500: // Internal server error; still worth retrying
      case 501: // Not implemented
      case 502: // Bad gateway
      case 503: // Unavailable temporarily
      case 504: // Timeout
      case CREATE_SALE_REQUEST_TIMEOUT_CODE:
        setSyncAttemptsAndStatus()
        break

      case ERROR_AS_SUCCESS_RESPONSE_CODE: // Unexpected/error response received as success response
        logError()
        sale.syncStatus = SaleSyncStatus.ERROR
        break

      default:
        // Unrecoverable error
        logError()
        sale.syncStatus = SaleSyncStatus.ERROR
        break
    }

    return this.salesStore
      .saveSale(sale)
      .then(() => $q.reject(`${errorMessage}[${status}]`))
  }

  /**
   * Handle a failure when creating sale by gql mutation.
   *
   * @param sale {Sale}
   *        The sale that was posted to graphter
   *
   * @param networkError {Any}
   *        The error.networkError object returned by the graphter
   *
   * @return {Promise} A promise resolved once the sale has been updated and saved to IndexedDB
   */
  private _handleCreateSaleFailure(
    sale: Sale,
    err: $TSFixMe
  ): IPromise<unknown> {
    const defaultMessage = 'Error received when creating sale'
    const { graphQLErrors = [] } = err
    const networkError = err.networkError || {}
    let { statusCode, result, message: gqlMessage } = networkError
    const customError = err.customError || {}
    const {
      code: customCode,
      message: customMessage,
      field: customField,
    } = customError

    let code: number = -1

    if (!err.networkError && graphQLErrors.length > 0) {
      // handle the graphQL errors
      code = 500
      gqlMessage = graphQLErrors[0].message
      result = graphQLErrors[0]
    } else if (err.networkError && graphQLErrors.length === 0) {
      // handle the network error
      // when statusCode and result are both empty, client is offline
      code = !statusCode && !result ? 0 : statusCode
    } else if (err.customError && graphQLErrors.length === 0) {
      if (
        customCode === SaleCreateErrorCode.EXISTS &&
        customField === SaleCreateErrorField.SALE_ID
      ) {
        // handle the duplicate sale error
        result = customMessage
        code = DUPLICATE_SALE_ID_RESPONSE_CODE
      }
      if (customCode === SaleCreateErrorCode.TIMEOUT) {
        result = customMessage
        code = CREATE_SALE_REQUEST_TIMEOUT_CODE
      }
    }

    const logError = (msg: string = defaultMessage) => {
      if (code === CREATE_SALE_REQUEST_TIMEOUT_CODE) {
        this._trackTimeoutUBE(
          this._prepareDataForLogging(sale),
          'Timeout when creating sale'
        )
      } else {
        const message = `[SaleSyncManager] ${msg} [${code}]`
        trackError(message, {
          data: {
            sale: JSON.stringify(this._prepareDataForLogging(sale)),
            responseCode: code,
            attempts: sale.syncAttempts,
            data: !statusCode && !result ? gqlMessage : result,
          },
        })
      }
    }

    const setSyncAttemptsAndStatus = (reset: boolean = false) => {
      if (reset) {
        // Clear syncAttemps and syncStatus before regenerating sale.localId
        sale.syncAttempts = 0
        this._resetSaleSyncStatus(sale)
        return
      }

      // To not trigger the resync if the first submission of submit-early-sale failed
      if (isFirstTimeSubmitEarly(sale)) {
        sale.syncStatus = SaleSyncStatus.MODIFIED
        // Clear invoice number in IndexedDB so retry gets fresh one from server.
        // Redux state is kept intact for printing (print reads from Redux, not IndexedDB).
        sale.receiptNumberDetails = undefined
        return
      }

      if (sale.syncStatus !== SaleSyncStatus.ERROR) {
        sale.syncAttempts++

        if (sale.syncAttempts === MAX_SYNC_RETRIES) {
          sale.syncStatus = SaleSyncStatus.ERROR
        } else {
          sale.syncStatus = SaleSyncStatus.ATTEMPTED
        }
      }

      logError()
    }

    const handleRegenerateSaleId = () => {
      const oldSaleId = sale.localId

      logError('Duplicated saleID generated')

      // Reset syncAttempts and syncStatus
      setSyncAttemptsAndStatus(true)

      // Regenerate localId and mutationID
      sale.localId = uuid()
      sale.mutationID = md5(
        JSON.stringify({
          ...sale,
          syncStatus: undefined,
          syncAttempts: undefined,
          mutationID: undefined,
          responseStatus: undefined,
          responseData: undefined,
        }) // empty the changeable fields during retries, only keep the stable info
      )

      this.vdTracking.trackUserEvent({
        eventName: 'transaction.sale.create.regenerate_id',
        metadata: {
          old_sale_id: oldSaleId,
          new_sale_id: sale.localId,
        },
      })

      // localId is the key of sales table in indexDB, to update the sale.localId
      // should remove the sale record with old localId then save the sale with regenerated localId
      return this.salesStore
        .removeSale(oldSaleId)
        .then(() => this.salesStore.saveSale(sale))
        .then(() => {
          const message = `Sale localId regenerated [${code}]`
          return $q.reject(new OverwritingSaleError(message, sale))
        })
    }

    this._trackFailedSale({ sale })
    ;(sale as $TSFixMe).responseStatus = code
    ;(sale as $TSFixMe).responseData = result

    switch (code) {
      case 0: // Client side offline
        if (
          !this.webRegisterStatusManager.isOnline() ||
          !this.vdWindowDelegate.isOnline
        ) {
          this._resetSaleSyncStatus(sale)
          this._incrementSalesMadeOffline(sale.localId)
          this._transitionOffline()
        } else {
          setSyncAttemptsAndStatus()
        }
        break
      case 422: // GRAPHQL_VALIDATION_FAILED
      case 500: // Internal server error; @Todo: more status to be added
      case CREATE_SALE_REQUEST_TIMEOUT_CODE:
        setSyncAttemptsAndStatus()
        break

      case DUPLICATE_SALE_ID_RESPONSE_CODE:
        return handleRegenerateSaleId()

      default:
        // Unrecoverable error
        logError()
        sale.syncStatus = SaleSyncStatus.ERROR
        break
    }

    return this.salesStore
      .saveSale(sale)
      .then(() => $q.reject(`${defaultMessage}[${code}]`))
  }

  private _trackTimeoutUBE(sale: Sale, eventName: string) {
    this.vdTracking.trackUserEvent({
      eventName,
      metadata: {
        saleId: sale.localId,
        userId: sale.user?.id,
        registerId: sale.registerId,
        attempts: sale.syncAttempts,
        submitSaleEarly: sale.isSubmittedSaleEarly,
        enabledDuplicateInvoiceMitigation: true,
        saleSyncStatus: sale.syncStatus,
      },
    })
  }

  /**
   * Reset submit-early-sale syncStatus in submission failed handler.
   *
   * @param sale {Sale}
   */
  private _resetSaleSyncStatus(sale: Sale) {
    if (isFirstTimeSubmitEarly(sale)) {
      sale.syncStatus = SaleSyncStatus.MODIFIED // No resync after first submission
      // Clear invoice number in IndexedDB so retry gets fresh one from server.
      // Redux state is kept intact for printing (print reads from Redux, not IndexedDB).
      sale.receiptNumberDetails = undefined
    } else {
      sale.syncStatus = SaleSyncStatus.UPLOAD
    }
  }

  /**
   * Check if a sale was made offline by checking if its localId is in the offline sales storage.
   *
   * @param saleId {string} The localId of the sale to check
   * @return {boolean} True if the sale was made offline, false otherwise
   */
  private _wasSaleMadeOffline(saleId: string): boolean {
    const salesMadeOffline = getFromStorage<OfflineSaleData>(
      OFFLINE_SALES_STORAGE_ID
    )
    return salesMadeOffline?.sales?.includes(saleId) || false
  }

  /**
   * Increment the total number of sales made during an offline period by adding the provided sale id to the
   * collection of sales already made.
   *
   * @param {String} saleId
   *        The id of the sale made whilst offline.
   */
  private _incrementSalesMadeOffline(saleId: string) {
    if (!saleId) {
      return
    }

    const salesMadeOffline =
      getFromStorage<OfflineSaleData>(OFFLINE_SALES_STORAGE_ID) || {}

    if (!salesMadeOffline.sales) {
      salesMadeOffline.sales = []
    }

    if (includes(salesMadeOffline.sales, saleId)) {
      return
    }

    salesMadeOffline.sales.push(saleId)
    saveToStorage(OFFLINE_SALES_STORAGE_ID, salesMadeOffline)
  }

  /**
   * Set the start date time for the current offline period (if not already set).
   */
  private _setOfflineStartTime() {
    const salesMadeOffline =
      getFromStorage<OfflineSaleData>(OFFLINE_SALES_STORAGE_ID) || {}

    if (salesMadeOffline.time) {
      return
    }

    salesMadeOffline.time = moment().format()
    saveToStorage(OFFLINE_SALES_STORAGE_ID, salesMadeOffline)
  }

  /**
   * Sends a UBE which tracks the number of sales made offline and the offline duration. Clears locally stored
   * data when complete in readiness for the next offline period.
   */
  private _trackSalesMadeOffline(): void {
    const salesMadeOffline = getFromStorage<OfflineSaleData>(
      OFFLINE_SALES_STORAGE_ID
    )

    if (!salesMadeOffline) {
      return
    }

    const sales = salesMadeOffline.sales

    if (sales && sales.length) {
      const offlineStartTime = salesMadeOffline.time
      let minutesOffline

      if (offlineStartTime) {
        const timeDifference = moment().diff(offlineStartTime)
        const duration = moment.duration(timeDifference)

        minutesOffline = round(
          duration.asMinutes(),
          VendNumber.ROUNDING_MODES.ROUND_DOWN
        )
      }

      this.vdTracking.trackUserEvent({
        eventName: 'Sales Made Offline',
        metadata: {
          count: sales.length,
          minutesOffline,
        },
      })
    }

    // Don't clear the data immediately - it will be cleared after all sales are processed
    // The data is needed for the _wasSaleMadeOffline() checks during sale sync
    if (!this.vdFeatureManager.get('track_offline_sale_metadata')) {
      // We're back online so clear tracking data for this offline period
      this._clearTrackingDataForOfflineSales()
    }
  }

  /**
   * Responds to the register transitioning back to an online state.
   */
  private _transitionOnline(): void {
    this._trackSalesMadeOffline()
    this._scheduleSyncRetry()
  }

  /**
   * Responds to the register transitioning to an offline state.
   */
  private _transitionOffline(): void {
    this._setOfflineStartTime()
    this._cancelScheduledSyncRetry()
  }

  /**
   * Clears the tacking data for offline sales from localStorage after all sales have been processed.
   */
  private _clearTrackingDataForOfflineSales(): void {
    removeFromStorage(OFFLINE_SALES_STORAGE_ID)
  }

  /**
   * Find lost/errored sales affected by (now fixed) known issues that can be fixed and cleaned up to be resent to
   * the server.
   *
   * @return {Promise} A promise resolved once any affected sales have been found and fixed
   */
  private _findAndFixLostOrErroredSales(): IPromise<void> {
    return this._findAndFixLostModifiedSales()
      .then(() => this.salesStore.getUnsyncedSales())
      .then(unsyncedSales =>
        this._findAndDeleteErroredBlankSales(unsyncedSales)
      )
      .then(unsyncedSales =>
        this._findAndFixDuplicatePaymentLocalIdSales(unsyncedSales)
      )
      .then(() => this._updateSyncStatusOfAllIncompleteSales())
  }

  /**
   * Updates the `syncStatus` of an incomplete sale (a blank sale or a sale missing a status and payments) to be
   * UPLOADED. This schedules it for removal after a week.
   *
   * @param {Sale} sale
   *        The sale to evaluate
   *
   * @return {Promise} A promise resolved once the sale has been updated.
   */
  private _updateStatusOfIncompleteSale(sale: Sale): IPromise<void> {
    return $q(resolve => {
      if (this._saleIsIncomplete(sale)) {
        // Since the sale is in an incomplete state, set its `syncStatus` to UPLOADED. UPLOADED sales are removed
        // from indexeddb after a week
        sale.syncStatus = SaleSyncStatus.UPLOADED

        this.salesStore.saveSale(sale).then(resolve)
      } else {
        resolve()
      }
    })
  }

  /**
   * Updates the `syncStatus` of incomplete sales from ERROR to UPLOADED. These are historical ERROR sales made
   * prior to the `_updateStatusOfIncompleteSale` method being added. @todo Since the `_updateStatusOfIncompleteSale`
   * method now ensures any new incomplete sales from ever having a ERROR status, this method can be removed when
   * its considered that all historical incomplete sales have been transitioned.
   *
   * @param {Sale} sale
   *        The sale to evaluate
   *
   * @return {Promise} A promise resolved once the sale has been updated.
   */
  private _updateSyncStatusOfAllIncompleteSales() {
    return this.salesStore
      .getSalesBySyncStatus(SaleSyncStatus.ERROR)
      .then(erroredSales => {
        if (erroredSales && erroredSales.length) {
          $q.all(
            erroredSales.map(sale => this._updateStatusOfIncompleteSale(sale))
          )
        }
      })
  }

  /**
   * Find errored sales still stuck in MODIFIED sync status and update their sync status to UPLOAD so they can be
   * retried.
   *
   * @return {Promise} A promise resolved once any affected sales have been found and fixed
   */
  private _findAndFixLostModifiedSales() {
    return this.salesStore
      .getSalesBySyncStatus(SaleSyncStatus.MODIFIED)
      .then(modifiedSales => {
        return $q.all(
          modifiedSales.map(sale => {
            const currentSaleId = this.posStateManager.getActiveSaleLocalId()
            if (sale.localId !== currentSaleId) {
              sale.syncStatus = sale.isEmptySale()
                ? SaleSyncStatus.ERROR
                : SaleSyncStatus.UPLOAD
              return $q.when(this.salesStore.saveSale(sale))
            }
            return undefined
          })
        )
      })
  }

  /**
   * Find any errored blank sales that were accidentally created and orphaned and remove them.
   *
   * @param  {Array<Sale>} unsyncedSales
   *         The collection of unsynced sales to search for errored blank sales
   *
   * @return {Promise} A promise resolved once any relevant sales have been found and removed
   */
  private _findAndDeleteErroredBlankSales(unsyncedSales: Sale[]) {
    const filteredUnsyncedSales: Sale[] = []
    return $q
      .all(
        unsyncedSales.map(sale => {
          if (sale.syncStatus === SaleSyncStatus.ERROR && sale.isEmptySale()) {
            return this.salesStore.removeSale(sale.localId)
          }
          filteredUnsyncedSales.push(sale)
          return undefined
        })
      )
      .then(() => filteredUnsyncedSales)
  }

  /**
   * Find any unsynced sales that have multiple payments (or a payment and change) with the same localId (as this is
   * probably the reason they have failed to sync).
   *
   * @param  {Array<Sale>} unsyncedSales
   *         The collection of unsynced sales to search for duplicate payment IDs
   *
   * @return {Promise} A promise resolved once any affected sales have been found and fixed
   */
  private _findAndFixDuplicatePaymentLocalIdSales(
    unsyncedSales: Sale[]
  ): IPromise<unknown> {
    return $q.all(
      unsyncedSales.map(sale => this._fixDuplicatePaymentLocalIds(sale))
    )
  }

  /**
   * Inspect the given sale for duplicate payment localIds and fix them up if applicable.
   *
   * @param  {Sale} sale
   *         The sale to inspect for duplicate payment IDs
   *
   * @return {Promise} A promise resolved once the sale has been updated, or instantly if all payment IDs are valid
   */
  private _fixDuplicatePaymentLocalIds(sale: Sale): IPromise<unknown> {
    const seenIds: string[] = []
    let fixApplied = false
    function processPaymentForDupeId(payment: Payment) {
      if (includes(seenIds, payment.localId)) {
        payment.localId = uuid()
        fixApplied = true
      } else {
        seenIds.push(payment.localId)
      }
    }

    each(sale.payments, processPaymentForDupeId)

    if (sale.change) {
      processPaymentForDupeId(sale.change)
    }

    if (fixApplied) {
      return this.salesStore.saveSale(sale)
    } else {
      return $q.resolve()
    }
  }

  /**
   * Get the list of unsynced sales. Also updates the unsynced sales count as well as the totals for attempted,
   * errored and upload sync statuses.
   *
   * @return {Promise<Array<Sale>>} A promise resolved with the list of unsynced sales
   */
  private _getUnsyncedSalesAndUpdateCounts(): IPromise<Sale[]> {
    return this.salesStore.getUnsyncedSales().then(sales => {
      // Only return sales that are considered complete. We don't want to expose incomplete errored sales.
      const unsyncedSales = sales.filter(sale => !this._saleIsIncomplete(sale))

      this._updateUnsyncedSalesCount(unsyncedSales)

      return unsyncedSales
    })
  }

  /**
   * Determine the number of unsynced sales by type. Either ATTEMPTED, ERROR or UPLOAD.
   *
   * @param {Array<Sale>} unsyncedSales
   *        Collection of unsynced sales
   *
   * @return {Object} Unsynced sales by content type
   */
  private _getUnsyncedSalesCountByType(unsyncedSales: Sale[]) {
    let attemptedSalesCount = 0
    let erroredSalesCount = 0
    let uploadSalesCount = 0

    unsyncedSales.forEach(unsyncedSale => {
      if (unsyncedSale.syncStatus === SaleSyncStatus.ATTEMPTED) {
        attemptedSalesCount++
      }

      if (unsyncedSale.syncStatus === SaleSyncStatus.ERROR) {
        erroredSalesCount++
      }

      if (unsyncedSale.syncStatus === SaleSyncStatus.UPLOAD) {
        uploadSalesCount++
      }
    })

    return {
      attemptedSalesCount,
      erroredSalesCount,
      uploadSalesCount,
    }
  }

  /**
   * Updates the count of all unsynced sales and emit the unsyncedSalesChange event.
   *
   * @param {Array<Sale>} unsyncedSales
   *        Collection of unsynced sales
   */
  private _updateUnsyncedSalesCount(unsyncedSales: Sale[]): void {
    const prevValue = this.unsyncedSaleCountByType
    const newValue = this._getUnsyncedSalesCountByType(unsyncedSales)

    if (!isEqual(prevValue, newValue)) {
      this._unsyncedSalesCount = unsyncedSales.length
      this._attemptedSalesCount = newValue.attemptedSalesCount
      this._erroredSalesCount = newValue.erroredSalesCount
      this._uploadSalesCount = newValue.uploadSalesCount

      this.emit('unsyncedSalesChange', {
        prevValue,
        newValue,
      })
    }

    saveToStorage(SALES_STATUS.VEND_ERROR_SALES_COUNT, this._erroredSalesCount)
  }

  /**
   * Handle a successful posting of a quote to the server. Update the uploaded quotes sale id to avoid sync issues
   *
   * @param sale {Sale}
   *        The sale that was posted to the server
   *
   * @param data {Object}
   *        The data returned by the server
   *
   * @return {Promise} A promise resolved once the sale has been updated and saved to IndexedDB
   */
  private async _handlePostQuoteSuccess(
    sale: Sale,
    data: $TSFixMe
  ): Promise<IPromise<unknown>> {
    if (!data || (data.errors && data.errors.length > 0)) {
      const message = '[SaleSyncManager] Quote post error received as a success'
      trackError(message, {
        data: {
          sale: sale.toJSON(),
          data,
        },
      })
      return this._handlePostQuoteFailure(sale, data)
    }

    const serverQuoteData = data.createQuote

    $log.debug(
      '[SaleSyncManager] Quote post succeeded, data returned: ',
      serverQuoteData
    )

    this.vdTracking.trackUserEvent({
      moduleName: 'Quotes',
      eventName: 'Complete Quote',
      metadata: {
        employeeId: sale.user?.id,
        employeeRole: sale.user?.roles?.map(role => role.name),
        wasEmailSent: sale.receiptEmailAddress.length > 0,
      },
    })

    if (await this.isRegisterAutoPrintEnabled(serverQuoteData.registerId)) {
      this.getQuoteForPrint({
        quoteId: serverQuoteData.id,
        templateId: sale.receiptID,
      }).catch(error =>
        this.vdToastNotificationService.negative({ message: error })
      )
    }

    this.posStateManager.resetPromotionsHelperValues()

    sale.syncStatus = SaleSyncStatus.UPLOADED

    return this.salesStore.saveSale(sale)
  }

  /**
   * Handle a failure when posting of a quote to the server.
   *
   * @param sale {Sale}
   *        The sale that was posted to the server
   *
   * @param data {Any}
   *        The data or error message returned by the server
   *
   *
   * @return {Promise} A promise resolved once the sale has been updated and saved to IndexedDB
   */
  private _handlePostQuoteFailure(
    sale: Sale,
    error: $TSFixMe
  ): IPromise<never> {
    const errorMessage = 'Failed to upload quote'

    const logError = () => {
      const message = `[SaleSyncManager] Quotes: ${errorMessage}`
      trackError(message, {
        data: {
          sale: sale.toJSON(),
          data: error,
          status: error.status,
        },
      })
    }

    const setSyncAttemptsAndStatus = () => {
      if (sale.syncStatus !== SaleSyncStatus.ERROR) {
        sale.syncAttempts++

        if (sale.syncAttempts === MAX_SYNC_RETRIES) {
          sale.syncStatus = SaleSyncStatus.ERROR
        } else {
          sale.syncStatus = SaleSyncStatus.ATTEMPTED
        }
      }

      logError()
    }

    this._trackFailedSale({ sale, eventName: 'Failed quote' })
    setSyncAttemptsAndStatus()
    return this.salesStore
      .saveSale(sale)
      .then(() => $q.reject(`${errorMessage} ${error.status || ''}`))
  }

  /**
   * Get quote by ID
   *
   * @param id String
   *        The ID of the quote to be retrieved
   * @returns {Quote} The quote object from the quotes service
   */

  public getQuoteById(id: string): IPromise<QuoteDataWithExtras> {
    return $q
      .resolve(
        client.query({
          query: quoteByIdQuery,
          variables: { quoteId: id },
        })
      )
      .then(response => this._handleGetQuoteByIdSuccess(id, response.data))
      .catch(response =>
        this._handleGetQuoteByIdFailure(id, response.data, response.status)
      )
  }

  /**
   * Handle a successful quote retrieval
   *
   * @param id String
   *        The ID of the requested quote
   * @param data {Any}
   *        The data or error message returned by the server
   * @returns {Promise} A promise resolved once the quote has been retrieved
   */
  private _handleGetQuoteByIdSuccess(
    id: string,
    data: any
  ): IPromise<QuoteDataWithExtras> {
    if (!data || (data.errors && data.errors.length > 0)) {
      return this._handleGetQuoteByIdFailure(
        id,
        data,
        ERROR_AS_SUCCESS_RESPONSE_CODE
      )
    }
    return data.quote
  }

  /**
   * Handle a failure when getting quote by ID
   *
   * @param id {String}
   *        The ID of the requested quote
   *
   * @param data {Any}
   *        The data or error message returned by the server
   *
   * @param status {Number}
   *        The HTTP status code of the response
   *
   * @return {Promise} A rejected promise with the error message
   */
  private _handleGetQuoteByIdFailure(
    id: string,
    data: any,
    status: number
  ): IPromise<never> {
    const errorMessage = 'Failed to retrieve quote'

    const message = `[SaleSyncManager] Quotes: ${errorMessage}`
    trackError(message, {
      data: {
        quoteId: id,
        responseCode: status,
        data,
      },
    })

    return $q.reject(`${errorMessage}[${status}]`)
  }

  /**
   *  Get the ready-to-print HTML representation of the quote
   *
   * @param obj {Object}
   *        Quote ID and the receipt template ID
   * @returns {Promise}
   */
  private getQuoteForPrint({
    quoteId,
    templateId,
  }: {
    quoteId: string
    templateId?: string
  }) {
    const template = templateId ? `?templateID=${templateId}&` : '?'

    return $http({
      method: 'GET',
      url: `/quote_receipt/${quoteId}${template}print=true`,
    })
      .then(response =>
        this._handleGetQuoteForPrintSuccess(quoteId, response.data)
      )
      .catch(response =>
        this._handleGetQuoteForPrintFailure(
          quoteId,
          response.data,
          response.status
        )
      )
  }

  private _handleGetQuoteForPrintSuccess(
    id: string,
    data: any
  ): IPromise<any> | void {
    $rootScope.$emit(EVENT.PRINT, {
      type: 'quote-print',
      data,
    })
  }

  private _handleGetQuoteForPrintFailure(
    id: string,
    data: any,
    status: number
  ): IPromise<never> {
    const errorMessage = `Failed to retrieving the quote (${id}) html for printing.`
    const userFriendlyErrorMessage =
      'Failed to print. Go to Quotes to print this quote.'
    const message = `[SaleSyncManager] Quotes: ${errorMessage} [${status}]`
    trackError(message, {
      data: {
        quoteId: id,
        responseCode: status,
        data,
      },
    })
    return $q.reject(userFriendlyErrorMessage)
  }

  /**
   * Get the receipt auto print setting for the register
   *
   * @param registerId {String}
   *        The desired register ID
   * @return {Promise<Boolean>} A promise resolved with a Boolean result.
   *         True if auto print is enabled, false otherwise
   */
  private isRegisterAutoPrintEnabled(registerId: string) {
    return this.registerManager
      .getRegisterById(registerId)
      .then(register => register?.print_receipt)
  }

  /**
   * Enrich sale data with address IDs and tokens for payments and fulfilment details
   *
   * @param saleMutationData {SaleMutationData}
   *        The base sale mutation data
   * @param billingAddressId {string}
   *        The billing address ID
   * @param billingAddressToken {string}
   *        The billing address token
   * @param shippingAddressId {string}
   *        The shipping address ID
   * @param shippingAddressToken {string}
   *        The shipping address token
   *
   * @return The enriched sale data with address information
   */
  private _enrichSaleDataWithAddressTokens(
    saleMutationData: SaleMutationData,
    billingAddressId: string,
    shippingAddressId: string,
    billingAddressToken?: string,
    shippingAddressToken?: string
  ) {
    return {
      ...saleMutationData,
      payments: saleMutationData.payments.map((payment: GqlPayment) => ({
        ...payment,
        billingAddressId,
        billingAddressToken,
      })),
      fulfilmentDetails: saleMutationData.fulfilmentDetails.map(
        (detail: GqlFulfilmentDetail) => ({
          ...detail,
          shippingAddressId,
          shippingAddressToken,
        })
      ),
    }
  }

  /**
   * Create sale with Phase 2 flow - includes address tokens in the sale creation request
   *
   * @param sale {Sale}
   *        The sale to create with delivery addresses
   * @param saleMutationData {SaleMutationData}
   *        The sale data in camelCase format for the mutation
   * @param response {Object}
   *        Response from createSaleAddresses mutation
   * @param options {Object}
   *        Options for the sale request
   *
   * @return {Promise} A promise resolved once the sale has been created with address tokens
   */
  private _createSaleWithAddressTokens(
    sale: Sale,
    saleMutationData: SaleMutationData,
    response: CreateSaleAddressesResponse,
    options: {
      isFirstTimeSubmitEarly: boolean
      isRetry?: boolean
      isCreatingNewSale: boolean
    }
  ): IPromise<unknown> {
    if (!response.data) {
      throw new Error('No data returned from createSaleAddresses mutation')
    }

    const {
      billingAddressId,
      shippingAddressId,
      shippingAddressToken,
      billingAddressToken,
      created,
      error,
    } = response.data.createSaleAddresses

    if (
      (!created ||
        !billingAddressId ||
        !shippingAddressId ||
        !billingAddressToken ||
        !shippingAddressToken) &&
      error &&
      error.code
    ) {
      throw error
    }

    const isRetrievedParkedSale = this._isRetrievedParkedSale(sale)

    if (isRetrievedParkedSale) {
      const baseSaleData = this.saleTransformer.toApi(sale, sale.lineItems)
      const saleData = {
        ...baseSaleData,
        client_id: this.posStateManager.getClientId() || '',
        register_sale_payments: baseSaleData.register_sale_payments.map(
          (payment: any) => ({
            ...payment,
            billing_address_id: billingAddressId,
            billing_address_token: billingAddressToken,
          })
        ),
        fulfillment_details: (sale.fulfilmentDetails ?? []).map(detail => ({
          type: detail.type,
          note: detail.note,
          shipping_address_id: shippingAddressId,
          shipping_address_token: shippingAddressToken,
        })),
      }
      return this._postSaleViaRestApi(sale, saleData, options)
    }

    const saleData = this._enrichSaleDataWithAddressTokens(
      saleMutationData,
      billingAddressId,
      shippingAddressId,
      billingAddressToken,
      shippingAddressToken
    )

    const createSaleRequest = $q.resolve(
      client.mutate({
        mutation: createSaleMutation,
        variables: {
          input: {
            ...saleData,
          },
        },
      })
    )

    return this._sendRegisterSaleRequest(createSaleRequest, options)
      .then(response => {
        const { invoiceNumber, error } = response?.data?.createSale
        try {
          if (!invoiceNumber && error && error.code) {
            throw error
          }
          return this._handleCreateSaleSuccess(sale, response?.data, saleData)
        } catch (err) {
          throw error
        }
      })
      .catch(err => {
        return this._handleCreateSaleFailure(sale, err)
      })
  }

  /**
   * Create sale with legacy flow - creates sale first, then links addresses separately
   *
   * @param sale {Sale}
   *        The sale to create with delivery addresses
   * @param saleMutationData {SaleMutationData}
   *        The sale data in camelCase format for the mutation
   * @param response {Object}
   *        Response from createSaleAddresses mutation
   * @param options {Object}
   *        Options for the sale request
   *
   * @return {Promise} A promise resolved once the sale has been created and addresses linked
   */
  private _createSaleAndLinkAddresses(
    sale: Sale,
    saleMutationData: SaleMutationData,
    response: CreateSaleAddressesResponse,
    options: {
      isFirstTimeSubmitEarly: boolean
      isRetry?: boolean
      isCreatingNewSale: boolean
    }
  ): IPromise<unknown> {
    if (!response.data) {
      throw new Error('No data returned from createSaleAddresses mutation')
    }

    const {
      billingAddressId,
      shippingAddressId,
      created,
      error,
    } = response.data.createSaleAddresses

    if (
      (!created || !billingAddressId || !shippingAddressId) &&
      error &&
      error.code
    ) {
      throw error
    }

    const linkAddresses = (saleId: string, invoiceNumber?: string) => {
      const linkSaleAddressRequest = $q.resolve(
        client.mutate({
          mutation: linkSaleAddressMutation,
          variables: {
            input: {
              billingAddressId,
              saleId,
              shippingAddressId,
              customerId: sale.customer?.id,
            },
          },
        })
      )
      return linkSaleAddressRequest
        .then(response => {
          const { created } = response?.data?.linkSaleAddress
          if (!created) {
            trackError('[SaleSyncManager] Failed to link sale address', {
              data: {
                saleId: sale.localId,
                invoiceNumber,
                billingAddressId,
                shippingAddressId,
              },
            })
          }
        })
        .catch(linkErr => {
          trackError('[SaleSyncManager] Link sale address API failed', {
            data: {
              saleId: sale.localId || '',
              customerId: sale.customer?.id || '',
              billingAddressId: billingAddressId || '',
              shippingAddressId: shippingAddressId || '',
              error: linkErr,
            },
          })
        })
    }

    // Retrieved parked sales already exist in the backend — use REST API to update
    // rather than createSaleMutation which would fail with an EXISTS error
    if (this._isRetrievedParkedSale(sale)) {
      const baseSaleData = this.saleTransformer.toApi(sale, sale.lineItems)
      const saleData = {
        ...baseSaleData,
        client_id: this.posStateManager.getClientId() || '',
      }
      return this._postSaleViaRestApi(sale, saleData, options).then(() =>
        linkAddresses(sale.id!)
      )
    }

    const createSaleRequest = $q.resolve(
      client.mutate({
        mutation: createSaleMutation,
        variables: {
          input: {
            ...saleMutationData,
          },
        },
      })
    )

    return this._sendRegisterSaleRequest(createSaleRequest, options)
      .then(response => {
        const { invoiceNumber, saleId, error } = response?.data?.createSale
        try {
          if (!invoiceNumber && error && error.code) {
            throw error
          }

          this._handleCreateSaleSuccess(sale, response?.data, saleMutationData)

          return linkAddresses(saleId, invoiceNumber)
        } catch (err) {
          return this._handleCreateSaleFailure(sale, err)
        }
      })
      .catch(err => {
        return this._handleCreateSaleFailure(sale, err)
      })
  }

  /**
   * Create sale for delivery with address creation and linking addresses to the sale
   *
   * @param sale {Sale}
   *        The sale to create with delivery addresses
   * @param saleMutationData {SaleMutationData}
   *        The sale data in camelCase format for the mutation
   * @param options {Object}
   *        Options for the sale request
   *
   * @return {Promise} A promise resolved once the sale and addresses have been created and linked
   */
  private _createSaleForDelivery(
    sale: Sale,
    saleMutationData: SaleMutationData,
    options: {
      isFirstTimeSubmitEarly: boolean
      isRetry?: boolean
      isCreatingNewSale: boolean
    }
  ): IPromise<unknown> {
    const saleAddressData: SaleAddressMutationData = this.saleTransformer.toSaleAddressMutation(
      sale.fulfilmentDetails,
      sale.customer?.id
    )

    const createSaleAddressesRequest = $q.resolve(
      client.mutate({
        mutation: createSaleAddressesMutation,
        variables: {
          input: {
            ...saleAddressData,
          },
        },
      })
    )

    return createSaleAddressesRequest
      .then(response => {
        // In the Saved sale, there is no payment object, so we don't send billingAddressId and billingAddressToken
        // As we need to send them for continuing save sale, we skip to use version 2 flow for saved sale
        const hasPayments = sale.payments && sale.payments.length > 0
        if (
          getFeatureFromCache(FEATURE_CAPTURE_ADDRESS_SELL_SCREEN_PHASE_2) &&
          hasPayments
        ) {
          return this._createSaleWithAddressTokens(
            sale,
            saleMutationData,
            response,
            options
          )
        } else {
          return this._createSaleAndLinkAddresses(
            sale,
            saleMutationData,
            response,
            options
          )
        }
      })
      .catch(err => {
        return this._handleCreateSaleFailure(sale, err)
      })
  }
}

/**
 * Responsible for syncing sales from the client to the server and keeping track of unsynced sales.
 *
 * @module saleSyncManager
 */
angular
  .module('webregister.managers')
  .factory('saleSyncManager', SaleSyncManager)
